import React from 'react';

import { type FallbackProps, withErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import SectionMessage from '@atlaskit/section-message';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import { type Message } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { LuminaInteractionHandler } from '@atlassian/lumina/interaction';

import { AppendicesView } from './appendices';
import { type ExtensionComponentProps, ContentRenderer } from './content-renderer';
import { SourcesContext } from './message-source-context';
import messages from './messages';
import { SourcesView } from './sources';

type MessageRendererProps = {
	message: Message;
	onClickSource?: (type: string, citedProduct: string | null, product: string | null) => void;
	onClickAppendix?: (type: string) => void;
	onClickResponseLink?: () => void;
	onClickSmartCard?: () => void;
	onClickCitation?: (index: number, citedProduct: string | null, product: string | null) => void;
	onContentSelected?: () => void;
	onContentCopied?: () => void;
	onCodeBlockCopied?: () => void;
	allowDownloadCodeBlock?: boolean;
	onAuthLinkClick?: (url: string, authType: '3p' | 'forge') => void;
	getOnViewAnalysisClick?: (idx: number) => () => void;
	onDeepLinkClick?: (url: string) => void;
	markdownData?: Record<string, string>;
	lightweightEncoding?: boolean;
	productKey: string;
	orgId?: string;
	siteId: string;
	aaid?: string;
	conversationId?: string;
	/**
	 * Whether the content is still streaming. This is used to throttle perf tracking across all render cycles to avoid over-reporting with avg render time.
	 * If this is false or undefined, the content is considered to be fully available and the time to render is tracked right away.
	 */
	contentStillStreaming?: boolean;
	/**
	 * Whether the content renderer is in mini-modal view. When true, embed cards will render as inline cards.
	 * When false or undefined, embed cards will render as embed cards.
	 */
	isMiniModal?: boolean;
	/**
	 * Function to check if an experiment is enabled. Passed down to content transformer plugins.
	 */
	isExperimentEnabled?: (experimentName: string, needsExposure?: boolean) => boolean;
	/**
	 * Optional ADF transformer function to apply after encoding.
	 * Use this to apply custom transformations to the ADF before rendering.
	 */
	adfTransformer?: (adf: DocNode) => DocNode;
	/**
	 * Optional callback to send link count metrics when ADF is ready.
	 * Called with streamingComplete status and the final ADF document.
	 */
	sendLinkCountMetric?: (streamingComplete: boolean, adf: DocNode) => void;
	/**
	 * Optional override for the extension node component.
	 * Defaults to InsightExtension which renders insight link cards and profile groups.
	 */
	extensionComponent?: React.ComponentType<ExtensionComponentProps>;
	/**
	 * Optional function to process URLs for cross-product interaction-tracking (XPC).
	 * Forwarded to `ContentRenderer`. Called with `stripOnly = true` for links rendered
	 * as smart links by the linking platform, and `stripOnly = false` for plain links.
	 */
	xpcWrapUrl?: (url: string, stripOnly: boolean) => string;
	/**
	 * Whether the current conversation is with a third-party agent.
	 * When true, expand-body content is rendered through the ADF pipeline
	 * (`ReactRenderer` over `encodeAdf(content)`) for full markdown support.
	 */
	tempIsThirdPartyAgent?: boolean;
	/**
	 * Optional callback invoked when an actionable follow-up question is clicked.
	 * Forwarded to `ContentRenderer` so the host can auto-submit the question.
	 */
	onFollowUpClick?: (question: string) => void;
	/** Optional callback for allowlisted interactions with inline Lumina content. */
	onLuminaInteraction?: LuminaInteractionHandler;
};

const MessageRendererFallback = ({ error }: FallbackProps) => {
	const { formatMessage } = useIntl();
	return (
		<React.Fragment>
			<SectionMessage title={formatMessage(messages.messageErrorTitle)} appearance="error">
				{formatMessage(messages.messageError)}
			</SectionMessage>
			<ExperienceFailure error={error} name={ExperienceName.MESSAGE_RENDERER} />
		</React.Fragment>
	);
};

const MessageRendererRaw = ({
	message,
	onClickSource,
	onClickAppendix,
	onClickResponseLink,
	onClickSmartCard,
	onClickCitation,
	onContentSelected,
	onContentCopied,
	onCodeBlockCopied,
	allowDownloadCodeBlock = false,
	onAuthLinkClick,
	getOnViewAnalysisClick,
	onDeepLinkClick,
	markdownData,
	lightweightEncoding,
	productKey,
	orgId,
	siteId,
	aaid,
	conversationId,
	contentStillStreaming,
	isMiniModal = false,
	isExperimentEnabled,
	adfTransformer,
	sendLinkCountMetric,
	extensionComponent,
	xpcWrapUrl,
	tempIsThirdPartyAgent,
	onFollowUpClick,
	onLuminaInteraction,
}: MessageRendererProps) => {
	const { content, sources, appendices } = message;

	const hasSources = !!sources && sources.length > 0;

	const sourceContextData = React.useMemo(() => ({ sources }), [sources]);

	if (!content) {
		return null;
	}
	return (
		<SourcesContext.Provider value={sourceContextData}>
			<React.Fragment>
				<ExperienceStart name={ExperienceName.MESSAGE_RENDERER} />
				{!!content && (
					<ContentRenderer
						content={content}
						lightweightEncoding={lightweightEncoding}
						onCodeBlockCopied={onCodeBlockCopied}
						allowDownloadCodeBlock={allowDownloadCodeBlock}
						onContentCopied={onContentCopied}
						onContentSelected={onContentSelected}
						onResponseLinkClick={onClickResponseLink}
						onSmartCardClick={onClickSmartCard}
						onCitationClick={onClickCitation}
						onAuthLinkClick={onAuthLinkClick}
						getOnViewAnalysisClick={getOnViewAnalysisClick}
						markdownData={markdownData}
						productKey={productKey}
						xpcWrapUrl={xpcWrapUrl}
						orgId={orgId}
						siteId={siteId}
						aaid={aaid}
						messageId={message.id}
						conversationId={conversationId}
						onDeepLinkClick={onDeepLinkClick}
						contentStillStreaming={contentStillStreaming}
						shouldOverrideCardAsInline={isMiniModal}
						isExperimentEnabled={isExperimentEnabled}
						adfTransformer={adfTransformer}
						sendLinkCountMetric={sendLinkCountMetric}
						extensionComponent={extensionComponent}
						tempIsThirdPartyAgent={tempIsThirdPartyAgent}
						onFollowUpClick={onFollowUpClick}
						onLuminaInteraction={onLuminaInteraction}
					/>
				)}
				{hasSources ? (
					<SourcesView
						sources={sources}
						onClickSource={onClickSource}
						conversationId={conversationId}
						siteId={siteId}
						productKey={productKey}
						xpcWrapUrl={xpcWrapUrl}
					/>
				) : null}
				{!!appendices && appendices.length > 0 && (
					<AppendicesView appendices={appendices} onClickAppendix={onClickAppendix} />
				)}
				<ExperienceSuccess name={ExperienceName.MESSAGE_RENDERER} />
			</React.Fragment>
		</SourcesContext.Provider>
	);
};

export const MessageRenderer: React.ForwardRefExoticComponent<
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	MessageRendererProps & React.RefAttributes<any>
> = withErrorBoundary(MessageRendererRaw, {
	FallbackComponent: MessageRendererFallback,
});
